import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, ScrollText, ShieldCheck } from "lucide-react";
import { PageHeader, Loader, Empty } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { fmtDate } from "@/lib/format";

export default function AdminMisc() {
  const { t } = useI18n();
  const [form, setForm] = useState({ title: "", body: "" });
  const [busy, setBusy] = useState(false);
  const [logs, setLogs] = useState(null);
  const [cap, setCap] = useState("");
  const [lockDays, setLockDays] = useState("");
  useEffect(() => {
    api.get("/admin/audit-logs").then(({ data }) => setLogs(data)).catch(() => {});
    api.get("/admin/settings").then(({ data }) => { setCap(String(data.deposit_cap)); setLockDays(String(data.capital_lock_days ?? 20)); }).catch(() => {});
  }, []);

  const saveCap = async () => {
    try {
      const { data } = await api.put(`/admin/settings?deposit_cap=${parseInt(cap, 10)}`);
      setCap(String(data.deposit_cap)); toast.success("Deposit cap updated");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const saveLock = async () => {
    try {
      const { data } = await api.put(`/admin/settings?capital_lock_days=${parseInt(lockDays, 10)}`);
      setLockDays(String(data.capital_lock_days)); toast.success("Capital lock period updated");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const broadcast = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/admin/notifications/broadcast", form);
      toast.success(data.message); setForm({ title: "", body: "" });
      api.get("/admin/audit-logs").then(({ data }) => setLogs(data));
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title={t("settings")} subtitle="Deposit cap, broadcast notifications & audit logs." />
      <div className="ec-card p-6 mb-8 max-w-md">
        <h3 className="font-head text-lg font-semibold text-white flex items-center gap-2 mb-3"><ShieldCheck className="w-5 h-5 text-orange-400" />Deposit Cap / Limit</h3>
        <p className="text-sm text-gray-400 mb-3">Maximum number of deposit requests each member can make. Members use this limit automatically.</p>
        <div className="flex gap-2">
          <Input type="number" value={cap} onChange={(e) => setCap(e.target.value)} data-testid="deposit-cap-input" className="w-32" />
          <Button onClick={saveCap} data-testid="deposit-cap-save" className="bg-orange-600 hover:bg-orange-500 btn-glow font-semibold">{t("save")}</Button>
        </div>
      </div>
      <div className="ec-card p-6 mb-8 max-w-md">
        <h3 className="font-head text-lg font-semibold text-white flex items-center gap-2 mb-3"><ShieldCheck className="w-5 h-5 text-orange-400" />Capital Lock Period</h3>
        <p className="text-sm text-gray-400 mb-3">Number of days a member's approved capital (deposit) stays locked before it becomes available for withdrawal. Default 20 days.</p>
        <div className="flex gap-2 items-center">
          <Input type="number" value={lockDays} onChange={(e) => setLockDays(e.target.value)} data-testid="capital-lock-input" className="w-32" />
          <span className="text-sm text-gray-400">days</span>
          <Button onClick={saveLock} data-testid="capital-lock-save" className="bg-orange-600 hover:bg-orange-500 btn-glow font-semibold">{t("save")}</Button>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-8 items-start">
        <form onSubmit={broadcast} className="ec-card p-6 space-y-4">
          <h3 className="font-head text-lg font-semibold text-white flex items-center gap-2"><Megaphone className="w-5 h-5 text-orange-400" />{t("broadcast")}</h3>
          <div><Label>{t("title")}</Label><Input data-testid="broadcast-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="mt-1" /></div>
          <div><Label>{t("message")}</Label><Textarea data-testid="broadcast-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required rows={4} className="mt-1" /></div>
          <Button type="submit" disabled={busy} data-testid="broadcast-send" className="bg-orange-600 hover:bg-orange-500 btn-glow font-semibold px-8">{busy ? "..." : t("send")}</Button>
        </form>

        <div className="ec-card p-6">
          <h3 className="font-head text-lg font-semibold text-white flex items-center gap-2 mb-4"><ScrollText className="w-5 h-5 text-orange-400" />{t("audit_logs")}</h3>
          {!logs ? <Loader /> : logs.length === 0 ? <Empty label="No logs." /> : (
            <div className="divide-y divide-white/5 max-h-96 overflow-y-auto">
              {logs.map((l) => (
                <div key={l.id} className="py-3 text-sm" data-testid={`audit-${l.id}`}>
                  <p className="text-white font-medium">{l.action}</p>
                  <p className="text-xs text-gray-500">{l.detail}</p>
                  <p className="text-xs text-gray-600">{fmtDate(l.created_at)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
